import { Suspense, ReactNode } from 'react';
import { WebGPUCanvas } from './WebGPUCanvas';
import { PerfToggles } from './PerfToggles';
import { useUIStore } from '@/state/useUIStore';

interface CanvasRootProps {
  children: ReactNode;
}

export function CanvasRoot({ children }: CanvasRootProps) {
  const showFps = useUIStore((s) => s.settings.showFps);
  const loadProgress = useUIStore((s) => s.loadProgress);
  const quality = useUIStore((s) => s.settings.graphicsQuality);
  // Determine pixel ratio based on quality setting
  const dpr: [number, number] = 
    quality === 'low' ? [0.5, 1] :
    quality === 'medium' ? [0.75, 1.5] :
    [1, 2];

  return (
    <div className="canvas-root">
      <WebGPUCanvas
        dpr={dpr}
        camera={{
          fov: 60,
          near: 1,
          far: 200000,
          position: [0, 150, 300],
        }}
        shadows={quality !== 'low'}
        onCreated={({ gl, camera }) => {
          const r = gl as any;
          gl.setClearColor('#000000');

          // Reverse-Z depth buffer for planetary-scale precision.
          // R3F v8 creates a default WebGLRenderer (can't await async factory),
          // so we manually replicate what reversedDepthBuffer: true does.
          if (r.isWebGLRenderer && r.state?.buffers?.depth?.setReversed) {
            // Reverse-Z: EXT_clip_control(ZERO_TO_ONE) + auto-reverse depth funcs.
            // setReversed() internally invalidates the depth-clear cache and
            // re-applies setClear so gl.clearDepth flips from 1 → 0.
            r.state.buffers.depth.setReversed(true);
            // Reversed projection matrix: near→1, far→0
            (camera as any)._reversedDepth = true;
            camera.updateProjectionMatrix();
          } else if (r.isWebGPURenderer) {
            (camera as any)._reversedDepth = true;
            camera.updateProjectionMatrix();
            r.setClearDepth(0);
          }
        }}
      >
        {/* Performance overlay — only after scene is fully loaded */}
        {showFps && loadProgress >= 100 && <PerfToggles />}

        {/* Scene content with suspense for async loading */}
        <Suspense fallback={null}>
          {children}
        </Suspense>
      </WebGPUCanvas>
    </div>
  );
}

// Styles
const styles = `
  .canvas-root {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: #000000;
  }
`;

if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = styles;
  document.head.appendChild(styleEl);
}
